import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Badge, Accordion } from 'react-bootstrap';
import { FaSave, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import dataService from '../services/dataService';
import aiServiceFactory from '../services/aiServiceFactory';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';
import { error as logError } from '../utils/logger';
import { apiUrl, getApiBase } from '../utils/apiUrl';

const Settings = ({ preferences, onUpdatePreferences, aiServiceStatus, onProviderChange }) => {
  const { language } = useLanguage();
  const [aiProvider, setAiProvider] = useState('groq');
  const [groqApiKey, setGroqApiKey] = useState('');
  const [alibabaApiKey, setAlibabaApiKey] = useState('');
  const [alibabaModel, setAlibabaModel] = useState('qwen-plus');
  const [configFromServer, setConfigFromServer] = useState(null);

  const [formData, setFormData] = useState({
    favorite_actors: [],
    favorite_genres: [],
    preferred_movie_types: [],
    rating_threshold: 7,
    max_recommendations: 20,
    language_preference: 'español',
    content_rating: 'PG-13',
    sorting_preferences: {
      default_sort: 'average_rating',
      available_sorts: ['user_rating', 'external_rating', 'ai_rating', 'average_rating'],
      weight_preferences: {
        user_rating_weight: 0.4,
        external_rating_weight: 0.3,
        ai_rating_weight: 0.3
      }
    }
  });
  const [newActor, setNewActor] = useState('');
  const [newGenre, setNewGenre] = useState('');
  const [newMovieType, setNewMovieType] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [groqQuota, setGroqQuota] = useState(null);
  const [alibabaStatus, setAlibabaStatus] = useState(null);
  const [loadingQuota, setLoadingQuota] = useState(false);
  const [loadingAlibaba, setLoadingAlibaba] = useState(false);

  useEffect(() => {
    if (preferences) {
      setFormData(preferences);
    }
  }, [preferences]);

  
  useEffect(() => {
    const ui = aiServiceFactory.getPreferredFromUI();
    fetch(apiUrl('/api/config'), { credentials: 'include' })
      .then((r) => r.ok ? r.json() : {})
      .then((c) => {
        setConfigFromServer(c);
        // Siempre usar Groq
        setAiProvider('groq');
        setGroqApiKey(ui.groqApiKey || c?.groqApiKey || '');
        setAlibabaApiKey(c?.alibabaApiKey || '');
        setAlibabaModel(c?.alibabaModel || 'qwen-plus');
        if (ui.groqApiKey || c?.groqApiKey) {
          loadGroqQuota();
        }
        if (c?.alibabaApiKey) {
          loadAlibabaStatus();
        }
      })
      .catch(() => {
        // Siempre usar Groq
        setAiProvider('groq');
        setGroqApiKey(ui.groqApiKey || '');
      });
  }, []);

  const loadAlibabaStatus = async () => {
    setLoadingAlibaba(true);
    try {
      const res = await fetch(apiUrl('/api/alibaba/status'), { credentials: 'include' });
      const data = await res.json();
      setAlibabaStatus(data);
      console.warn('[MisKosas Alibaba]', JSON.stringify(data));
    } catch (err) {
      logError('Error loading Alibaba status:', err);
    } finally {
      setLoadingAlibaba(false);
    }
  };

  const loadGroqQuota = async () => {
    setLoadingQuota(true);
    try {
      const res = await fetch(apiUrl('/api/groq/quota'), { credentials: 'include' });
      const data = await res.json();
      setGroqQuota(data);
      console.warn('[MisKosas Groq] quota (también en logs PM2 como [Groq status])', JSON.stringify(data));
    } catch (err) {
      logError('Error loading Groq quota:', err);
      console.warn('[MisKosas Groq] quota error', err.message);
    } finally {
      setLoadingQuota(false);
    }
  };

  const handleProviderSave = async () => {
    try {
      await fetch(apiUrl('/api/me/settings'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groq_api_key: groqApiKey.trim() || null,
          alibaba_api_key: alibabaApiKey.trim() || null,
          alibaba_model: alibabaModel.trim() || null
        }),
        credentials: 'include'
      });
    } catch (e) {}
    aiServiceFactory.setPreferredProvider('groq', groqApiKey.trim());
    if (onProviderChange) onProviderChange();
    if (groqApiKey.trim()) {
      setTimeout(loadGroqQuota, 1000);
    } else {
      setGroqQuota(null);
    }
    if (alibabaApiKey.trim()) {
      setTimeout(loadAlibabaStatus, 1000);
    } else {
      setAlibabaStatus(null);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWeightChange = (weightType, value) => {
    setFormData(prev => ({
      ...prev,
      sorting_preferences: {
        ...prev.sorting_preferences,
        weight_preferences: {
          ...prev.sorting_preferences.weight_preferences,
          [weightType]: parseFloat(value)
        }
      }
    }));
  };

  const addToList = (listType, value, setter) => {
    if (value.trim() && !formData[listType].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [listType]: [...prev[listType], value.trim()]
      }));
      setter('');
    }
  };

  const removeFromList = (listType, item) => {
    setFormData(prev => ({
      ...prev,
      [listType]: prev[listType].filter(i => i !== item)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setMessage(null);

    try {
      // Guardar preferencias y mostrar contenido en consola
      const success = await dataService.updateUserPreferences(formData, true);
      if (success) {
        setMessage({ 
          type: 'success', 
          text: t('settings.preferencesSaved', language)
        });
        // También actualizar el estado local
        if (onUpdatePreferences) {
          onUpdatePreferences(formData);
        }
      } else {
        setMessage({ type: 'danger', text: t('settings.errorSaving', language) });
      }
    } catch (error) {
      setMessage({ type: 'danger', text: t('settings.errorSaving', language) });
    } finally {
      setSaving(false);
    }
  };


  const renderListInput = (label, listType, value, setter, placeholder) => (
    <div className="mb-3">
      <Form.Label>{label}</Form.Label>
      <div className="d-flex mb-2">
        <Form.Control
          type="text"
          value={value}
          onChange={(e) => setter(e.target.value)}
          placeholder={placeholder}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addToList(listType, value, setter);
            }
          }}
        />
          <Button
            variant="outline-primary"
            onClick={() => addToList(listType, value, setter)}
            className="ms-2"
          >
            {t('settings.add', language)}
          </Button>
      </div>
      <div className="d-flex flex-wrap gap-1">
        {formData[listType].map((item, index) => (
          <Badge
            key={index}
            bg="primary"
            className="d-flex align-items-center"
          >
            {item}
            <Button
              variant="link"
              size="sm"
              className="text-white p-0 ms-1"
              onClick={() => removeFromList(listType, item)}
            >
              ×
            </Button>
          </Badge>
        ))}
      </div>
    </div>
  );

  return (
    <div className="settings-page">
      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)} className="mb-3">
          {message.text}
        </Alert>
      )}

      <Accordion className="settings-accordion mb-3">
        <Accordion.Item eventKey="preferences">
          <Accordion.Header>{t('settings.sectionPreferences', language)}</Accordion.Header>
          <Accordion.Body>
                {renderListInput(
                  t('settings.favoriteActors', language),
                  'favorite_actors',
                  newActor,
                  setNewActor,
                  t('settings.actorNamePlaceholder', language)
                )}

                {/* Géneros favoritos */}
                <div className="mb-3">
                  <Form.Label>
                    {t('settings.favoriteGenres', language)}
                    <small className="text-muted ms-2">{t('settings.favoriteGenresExample', language)}</small>
                  </Form.Label>
                  <div className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      value={newGenre}
                      onChange={(e) => setNewGenre(e.target.value)}
                      placeholder={t('settings.genreNamePlaceholder', language)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addToList('favorite_genres', newGenre, setNewGenre);
                        }
                      }}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={() => addToList('favorite_genres', newGenre, setNewGenre)}
                      className="ms-2"
                    >
                      {t('settings.add', language)}
                    </Button>
                  </div>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {formData.favorite_genres.map((item, index) => (
                      <Badge
                        key={index}
                        bg="primary"
                        className="d-flex align-items-center"
                      >
                        {item}
                        <Button
                          variant="link"
                          size="sm"
                          className="text-white p-0 ms-1"
                          onClick={() => removeFromList('favorite_genres', item)}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {t('settings.favoriteGenresDescription', language)}
                  </Form.Text>
                </div>

                {/* Tipos de películas preferidos */}
                <div className="mb-3">
                  <Form.Label>
                    {t('settings.preferredMovieTypes', language)}
                    <small className="text-muted ms-2">{t('settings.preferredMovieTypesExample', language)}</small>
                  </Form.Label>
                  <div className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      value={newMovieType}
                      onChange={(e) => setNewMovieType(e.target.value)}
                      placeholder={t('settings.movieTypePlaceholder', language)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addToList('preferred_movie_types', newMovieType, setNewMovieType);
                        }
                      }}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={() => addToList('preferred_movie_types', newMovieType, setNewMovieType)}
                      className="ms-2"
                    >
                      {t('settings.add', language)}
                    </Button>
                  </div>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {formData.preferred_movie_types.map((item, index) => (
                      <Badge
                        key={index}
                        bg="primary"
                        className="d-flex align-items-center"
                      >
                        {item}
                        <Button
                          variant="link"
                          size="sm"
                          className="text-white p-0 ms-1"
                          onClick={() => removeFromList('preferred_movie_types', item)}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {t('settings.preferredMovieTypesDescription', language)}
                  </Form.Text>
                </div>

                {/* Configuraciones numéricas */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('settings.minimumRating', language)}</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="10"
                        value={formData.rating_threshold}
                        onChange={(e) => handleInputChange('rating_threshold', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">
                        {t('settings.minimumRatingDescription', language)}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('settings.maxRecommendations', language)}</Form.Label>
                      <Form.Control
                        type="number"
                        min="5"
                        max="50"
                        value={formData.max_recommendations}
                        onChange={(e) => handleInputChange('max_recommendations', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Preferencias de idioma y clasificación */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('settings.preferredLanguage', language)}</Form.Label>
                      <Form.Select
                        value={formData.language_preference}
                        onChange={(e) => handleInputChange('language_preference', e.target.value)}
                      >
                        <option value="español">{t('settings.spanishOption', language)}</option>
                        <option value="inglés">{t('settings.englishOption', language)}</option>
                        <option value="cualquiera">{t('settings.anyLanguage', language)}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('settings.contentRating', language)}</Form.Label>
                      <Form.Select
                        value={formData.content_rating}
                        onChange={(e) => handleInputChange('content_rating', e.target.value)}
                      >
                        <option value="G">{t('settings.contentRatingG', language)}</option>
                        <option value="PG">{t('settings.contentRatingPG', language)}</option>
                        <option value="PG-13">{t('settings.contentRatingPG13', language)}</option>
                        <option value="R">{t('settings.contentRatingR', language)}</option>
                        <option value="NC-17">{t('settings.contentRatingNC17', language)}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="weights">
          <Accordion.Header>{t('settings.sectionRatingWeights', language)}</Accordion.Header>
          <Accordion.Body>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.userRatingWeight', language)}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            value={formData.sorting_preferences.weight_preferences.user_rating_weight}
                            onChange={(e) => handleWeightChange('user_rating_weight', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.externalRatingWeight', language)}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            value={formData.sorting_preferences.weight_preferences.external_rating_weight}
                            onChange={(e) => handleWeightChange('external_rating_weight', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.aiRatingWeight', language)}</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            value={formData.sorting_preferences.weight_preferences.ai_rating_weight}
                            onChange={(e) => handleWeightChange('ai_rating_weight', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Text className="text-muted">
                      {tf('settings.weightsDescription', {
                        total: (
                          formData.sorting_preferences.weight_preferences.user_rating_weight +
                          formData.sorting_preferences.weight_preferences.external_rating_weight +
                          formData.sorting_preferences.weight_preferences.ai_rating_weight
                        ).toFixed(1)
                      }, language)}
            </Form.Text>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="ai">
          <Accordion.Header>{t('settings.aiProviderTitle', language)}</Accordion.Header>
          <Accordion.Body>
              <Alert variant="info" className="mb-3">
                <strong>{t('settings.aiProvider', language)}:</strong> {t('settings.aiProviderDescription', language)}
                <br />
                <small>{t('settings.aiProviderNote', language)}</small>
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label>{t('settings.groqApiKey', language)}</Form.Label>
                <Form.Control
                  type="password"
                  placeholder={configFromServer?.groqApiKey ? t('settings.groqApiKeyConfigured', language) : t('settings.groqApiKeyPlaceholder', language)}
                  value={groqApiKey}
                  onChange={(e) => setGroqApiKey(e.target.value)}
                />
                {!(groqApiKey || configFromServer?.groqApiKey) && (
                  <Form.Text className="text-muted">
                    {t('settings.noApiKey', language)}{' '}
                    <a 
                      href="https://console.groq.com/keys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary"
                    >
                      {t('settings.getApiKey', language)}
                    </a>
                    {' '}{t('settings.getApiKeyNote', language)}
                  </Form.Text>
                )}
              </Form.Group>

              <hr className="my-3" />
              <h6 className="mb-2">{t('settings.alibabaSectionTitle', language)}</h6>
              <Form.Group className="mb-3">
                <Form.Label>{t('settings.alibabaApiKey', language)}</Form.Label>
                <Form.Control
                  type="password"
                  placeholder={
                    configFromServer?.alibabaApiKey
                      ? t('settings.alibabaApiKeyConfigured', language)
                      : t('settings.alibabaApiKeyPlaceholder', language)
                  }
                  value={alibabaApiKey}
                  onChange={(e) => setAlibabaApiKey(e.target.value)}
                />
                <Form.Text className="text-muted">
                  <a
                    href="https://modelstudio.console.alibabacloud.com/?tab=globalset#/api-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                  >
                    {t('settings.alibabaGetApiKey', language)}
                  </a>
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('settings.alibabaModel', language)}</Form.Label>
                <Form.Control
                  type="text"
                  list="alibaba-model-suggestions"
                  value={alibabaModel}
                  onChange={(e) => setAlibabaModel(e.target.value)}
                  placeholder="qwen3.5-27b"
                />
                <datalist id="alibaba-model-suggestions">
                  {(configFromServer?.alibabaSuggestedModels || [
                    'qwen-plus',
                    'qwen-turbo',
                    'qwen-max',
                    'qwen-flash',
                    'qwen3.5-27b'
                  ]).map((modelId) => (
                    <option key={modelId} value={modelId} />
                  ))}
                </datalist>
                <Form.Text className="text-muted">{t('settings.alibabaModelHelp', language)}</Form.Text>
              </Form.Group>

              <Button variant="primary" size="sm" onClick={handleProviderSave}>
                {t('settings.saveApiKey', language)}
              </Button>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="status">
          <Accordion.Header>{t('settings.systemStatus', language)}</Accordion.Header>
          <Accordion.Body>
              <div className="d-flex align-items-center mb-3">
                {aiServiceStatus ? (
                  <>
                    <FaCheckCircle className="text-success me-2" />
                    <span>{t('settings.aiConnected', language)}</span>
                  </>
                ) : (
                  <>
                    <FaTimesCircle className="text-danger me-2" />
                    <span>{t('settings.aiDisconnected', language)}</span>
                  </>
                )}
              </div>
              
              {/* Quota de Groq - Siempre visible ya que todos usan Groq */}
              <div className="mb-3">
                <h6 className="mb-2">{t('settings.groqQuota', language)}</h6>
                {loadingQuota ? (
                  <small className="text-muted">{t('settings.checkingQuota', language)}</small>
                ) : groqQuota ? (
                  groqQuota.available ? (
                    <div>
                      <Badge bg={groqQuota.rateLimitRemaining && groqQuota.rateLimitRemaining < 10 ? 'warning' : 'success'} className="me-2">
                        {t('settings.groqAvailable', language)}
                      </Badge>
                      {groqQuota.rateLimitRemaining !== null && (
                        <small className="text-muted">
                          {groqQuota.rateLimitRemaining > 0 ? (
                            <>{tf('settings.requestsRemaining', { remaining: groqQuota.rateLimitRemaining, limit: groqQuota.rateLimitLimit ? ` de ${groqQuota.rateLimitLimit}` : '' }, language)}</>
                          ) : (
                            <>{t('settings.noRequestsAvailable', language)}</>
                          )}
                        </small>
                      )}
                      {groqQuota.message && (
                        <div className="mt-1">
                          <small className="text-muted">{groqQuota.message}</small>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Alert variant={groqQuota.limitReached ? 'danger' : 'warning'} className="small mb-0 py-2">
                      <strong>{groqQuota.message || t('settings.groqNotAvailable', language)}</strong>
                      {groqQuota.retryAfter && (
                        <div className="mt-1">{tf('settings.retryAfter', { seconds: groqQuota.retryAfter }, language)}</div>
                      )}
                      {groqQuota.paymentRequired && (
                        <div className="mt-1">{t('settings.paymentRequired', language)}</div>
                      )}
                    </Alert>
                  )
                ) : (
                  <div>
                    <small className="text-muted">
                      {groqApiKey || configFromServer?.groqApiKey 
                        ? t('settings.updateQuotaToSee', language)
                        : t('settings.configureApiKeyToSee', language)}
                    </small>
                  </div>
                )}
                <Button variant="outline-secondary" size="sm" className="mt-2" onClick={loadGroqQuota} disabled={loadingQuota}>
                  {loadingQuota ? t('settings.checking', language) : t('settings.updateQuota', language)}
                </Button>
              </div>

              <div className="mb-3">
                <h6 className="mb-2">{t('settings.alibabaStatus', language)}</h6>
                {loadingAlibaba ? (
                  <small className="text-muted">{t('settings.checking', language)}</small>
                ) : alibabaStatus ? (
                  alibabaStatus.available ? (
                    <div>
                      <Badge bg="success" className="me-2">
                        {t('settings.alibabaAvailable', language)}
                      </Badge>
                      {alibabaStatus.message && (
                        <small className="text-muted d-block mt-1">{alibabaStatus.message}</small>
                      )}
                    </div>
                  ) : (
                    <Alert variant="warning" className="small mb-0 py-2">
                      {alibabaStatus.message || t('settings.alibabaNotAvailable', language)}
                    </Alert>
                  )
                ) : (
                  <small className="text-muted">{t('settings.alibabaConfigureKey', language)}</small>
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-2"
                  onClick={loadAlibabaStatus}
                  disabled={loadingAlibaba}
                >
                  {loadingAlibaba ? t('settings.checking', language) : t('settings.updateAlibabaStatus', language)}
                </Button>
              </div>

              {!aiServiceStatus && (
                <Alert variant="warning" className="small">
                  <strong>{t('common.note', language)}:</strong> {t('settings.noAIServiceNote', language)}
                </Alert>
              )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="summary">
          <Accordion.Header>{t('settings.preferencesSummary', language)}</Accordion.Header>
          <Accordion.Body>
              <div className="mb-2">
                <strong>{t('settings.actors', language)}:</strong> {formData.favorite_actors.length}
              </div>
              <div className="mb-2">
                <strong>{t('settings.genres', language)}:</strong> {formData.favorite_genres.length}
              </div>
              <div className="mb-2">
                <strong>{t('settings.movieTypes', language)}:</strong> {formData.preferred_movie_types.length}
              </div>
              <div className="mb-2">
                <strong>{t('settings.minimumRatingLabel', language)}:</strong> {formData.rating_threshold}/10
              </div>
              <div className="mb-2">
                <strong>{t('settings.maxRecommendationsLabel', language)}:</strong> {formData.max_recommendations}
              </div>
              <div className="mb-2">
                <strong>{t('settings.languageLabel', language)}:</strong> {formData.language_preference}
              </div>
              <div>
                <strong>{t('settings.ratingLabel', language)}:</strong> {formData.content_rating}
              </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Button
        type="button"
        variant="primary"
        disabled={saving}
        className="w-100 settings-page__save"
        onClick={handleSubmit}
      >
        {saving ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            {t('settings.saving', language)}
          </>
        ) : (
          <>
            <FaSave className="me-2" />
            {t('settings.savePreferences', language)}
          </>
        )}
      </Button>
    </div>
  );
};

export default Settings;
